/**
 * #1255 / #1389 / #1398 — the full injury report, in three places: the Game
 * Detail panel's Injuries disclosure (prop statuses on that page point here),
 * the board's inline `BoardInjuryReport` (a board entry opens it; see below)
 * and the Moneyline section's held report line (`SectionInjuries`), shown with
 * the opt-in injury panels.
 *
 * Closed by default; bounded and internally scrolling when open. The toggle is
 * a real button exposing `aria-expanded` (W3C disclosure pattern), not a
 * `<summary>`: the board's global hotkey listener does not recognise a summary
 * as a key owner, so Space on it could run Pull All on the aimed card and
 * Escape could clear the aim. The whole disclosure carries `data-lt2-owner`,
 * so Space scrolls or toggles natively, a letter is refused out loud instead
 * of trading, and Escape is answered here: after an explicit jump it returns
 * focus to the control that asked, and it never closes the game or ticket.
 *
 * Live updates re-render in place without moving focus and without a live
 * region over the list; only a meaningful connection warning uses the polite
 * status channel. Report age and connection health stay separate signals.
 *
 * `BoardInjuryReport` is the same report opened inline in the board for one
 * chosen game (a board entry's disclosure). It is read-only and touches no
 * ticket state: the panel's expanded game, trade intent and LT2 aim are
 * unchanged by opening, reading or closing it — a cross-game read must never
 * cost the trader the ticket they have open on another game.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { ChevronRight } from 'lucide-react';
import { coreGameStateKey, parseGameIdentity } from '@/lib/gameState/admission';
import { baseGameKey } from '@/services/analyticsGameKey';
import type { InjuryBlock, InjuryView } from '@/lib/gameState/injuries';
import {
  injuryEvidenceWarning, injurySourceLabel, reportAgeShort, reportAgeWords, reportClockEt, reportClockLocal,
  reportStampEt, sortInjuryRows, teamInjuryShort, teamInjuryState, teamInjuryWords,
} from '@/lib/gameState/injurySignals';
import type { GameStateConnectionStatus } from '@/lib/gameState/transport';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { cn } from '@/lib/infra/utils';
import { useCoreGameStateInterest, useCoreGameStatesStore } from '@/services/coreGameStates';
import {
  takeInjuryDisclosureOrigin, useInjuryDisclosureStore, type InjuryDisclosureRequest,
} from '@/stores/injuryDisclosure';

const NO_TEAMS: readonly string[] = [];

/** The block's teams, away first when the caller knows the order. */
function orderedTeams(block: InjuryBlock, order: readonly (string | null | undefined)[] | undefined): string[] {
  const codes = Object.keys(block.teams);
  const known = (order ?? []).filter((c): c is string => !!c && codes.includes(c));
  return [...known, ...codes.filter((c) => !known.includes(c))];
}

/** The report itself — source, exact ET and local clock, age, then each team
 *  and its rows by severity. Shared by the panel disclosure and the board. */
function InjuryReportBody({ block, now, codes, target }: {
  block: InjuryBlock;
  now: number;
  codes: readonly string[];
  target: { playerKey: string | null; team: string | null } | null;
}) {
  return <>
    <p className="text-muted-foreground">
      {injurySourceLabel(block.source)} <time dateTime={block.reportAt}>{reportClockEt(block.reportAt)}</time>
      {' '}({reportClockLocal(block.reportAt)} local) · {reportAgeWords(block.reportAt, now)}
    </p>
    {codes.map((code) => {
      const team = block.teams[code];
      const state = teamInjuryState(block, code);
      const teamTargeted = target?.team === code && !target.playerKey;
      return (
        <section key={code} className="mt-2" aria-label={`${code} injury report`} aria-current={teamTargeted || undefined}>
          <h4 className="font-semibold">{code} <span className="text-muted-foreground font-normal">· {teamInjuryWords(state)}</span></h4>
          {team.players.length > 0 && <ul className="mt-1 space-y-2">
            {sortInjuryRows(team.players).map((player, index) => {
              const targeted = !!target?.playerKey && player.playerKey === target.playerKey;
              return (
                <li
                  key={`${player.playerKey ?? player.player}-${index}`}
                  data-player-key={player.playerKey}
                  aria-current={targeted || undefined}
                  className={targeted ? 'ring-primary/60 rounded ring-1' : undefined}
                >
                  <div><span className="font-medium">{player.player}</span> · {player.statusRaw}</div>
                  {player.reason && <p className="text-muted-foreground">{player.reason}</p>}
                  <div className="text-muted-foreground text-[10px]">
                    Status since <time dateTime={player.statusSince}>{reportStampEt(player.statusSince)}</time>
                    {player.pairing !== 'paired' && ` · ${player.pairing === 'ambiguous' ? 'Ambiguous player match' : 'Not linked to a prop player'}`}
                  </div>
                  {player.source !== block.source && <div className="text-muted-foreground">{player.source}</div>}
                </li>
              );
            })}
          </ul>}
        </section>
      );
    })}
  </>;
}

export function GameInjuriesView({ row, status, now, request = null, teams, held = false, paneled = NO_TEAMS }: {
  row?: InjuryView;
  status: GameStateConnectionStatus;
  now: number;
  /** An explicit jump addressed to this game (see stores/injuryDisclosure). */
  request?: InjuryDisclosureRequest | null;
  /** [away, home] codes, for display order only. */
  teams?: readonly (string | null | undefined)[];
  /** #1398 — the Moneyline section's line: one held, truncating line above the
   *  table, so a changing summary or warning never moves the ladders below. */
  held?: boolean;
  /** #1400 — teams whose ladder panel is drawn: the line leaves their counts
   *  to the panel and names the report's exact slot instead. */
  paneled?: readonly string[];
}) {
  const block = row?.block ?? null;
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<{ playerKey: string | null; team: string | null } | null>(null);
  const [jump, setJump] = useState(0);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  const originRef = useRef<HTMLElement | null>(null);
  const handled = useRef(0);
  const regionId = useId();

  useEffect(() => {
    if (!request || request.seq === handled.current) return;
    handled.current = request.seq;
    originRef.current = takeInjuryDisclosureOrigin();
    // Answered once: a later remount of this panel must not jump again.
    if (useInjuryDisclosureStore.getState().request?.seq === request.seq) useInjuryDisclosureStore.setState({ request: null });
    setOpen(true);
    setTarget({ playerKey: request.playerKey, team: request.team });
    setJump(request.seq);
  }, [request]);

  // After the jump renders open: bring the section into view and focus its toggle.
  useLayoutEffect(() => {
    if (!jump) return;
    const toggle = toggleRef.current;
    toggle?.scrollIntoView?.({ block: 'nearest' });
    toggle?.focus({ preventScroll: true });
    const region = regionRef.current;
    const hit = region?.querySelector<HTMLElement>('[aria-current="true"]');
    hit?.scrollIntoView?.({ block: 'nearest' });
  }, [jump]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') return;
    // Answered here, never by the board: Escape must not clear the aim.
    e.stopPropagation();
    e.preventDefault();
    const origin = originRef.current;
    originRef.current = null;
    if (origin?.isConnected) { origin.focus(); return; }
    if (regionRef.current?.contains(e.target as Node)) { toggleRef.current?.focus(); return; }
    toggleRef.current?.blur();
  };

  const codes = block ? orderedTeams(block, teams) : [];
  const warning = block ? injuryEvidenceWarning(row, status, now) : null;
  const teamPart = (list: readonly string[]) => list.map((c) => `${c} ${teamInjuryShort(teamInjuryState(block!, c))}`);
  const fullSummary = !block ? 'Report unavailable'
    : [...teamPart(codes), `report ${reportAgeShort(block.reportAt, now)}`].join(' · ');
  const unpaneled = codes.filter((c) => !paneled.includes(c));
  const summary = !block || unpaneled.length === codes.length ? fullSummary
    : [...teamPart(unpaneled), `report ${reportClockEt(block.reportAt)} · ${reportAgeShort(block.reportAt, now)}`].join(' · ');

  return (
    <div
      className={cn('border-border min-w-0 shrink-0 text-xs', held ? 'border-b' : 'border-t')}
      data-lt2-owner=""
      data-testid={held ? 'section-injuries' : 'game-injuries'}
      onKeyDown={onKeyDown}
    >
      <div className={held
        ? 'flex h-7 min-w-0 flex-nowrap items-center gap-x-2 px-4'
        : 'flex min-w-0 flex-wrap items-baseline gap-x-2 px-3 py-2'}>
        <button
          ref={toggleRef}
          type="button"
          aria-expanded={open}
          aria-controls={regionId}
          onClick={() => setOpen((v) => !v)}
          title={held ? `Injuries ${fullSummary}${warning ? ` · ${warning}` : ''}` : undefined}
          className={cn(
            'min-w-0 text-left focus-visible:outline focus-visible:outline-1',
            // Held: the button fills the line, so its hit area never changes
            // with the summary, and the warning rides inside it.
            held ? 'flex-1 truncate whitespace-nowrap' : 'break-words',
          )}
        >
          {held && (
            <ChevronRight
              size={12}
              aria-hidden
              className={cn('text-muted-foreground mr-1 inline-block align-[-2px] transition-transform motion-reduce:transition-none', open && 'rotate-90')}
            />
          )}
          <span className="font-semibold">Injuries</span>
          <span className="text-muted-foreground ml-2">{summary}</span>
          {held && warning && <span aria-hidden className="ml-2 text-amber-500 light:text-amber-700">{warning}</span>}
        </button>
        {warning && <span role="status" className={held ? 'sr-only' : 'text-amber-500 light:text-amber-700'}>{warning}</span>}
      </div>
      {open && (
        <div
          ref={regionRef}
          id={regionId}
          role="region"
          aria-label="Injury report details"
          tabIndex={0}
          className={cn('max-h-64 overflow-auto overscroll-contain pb-2 break-words [overflow-wrap:anywhere]', held ? 'px-4' : 'px-3')}
        >
          {!block ? <p className="text-muted-foreground">No injury report is available for this game.</p>
            : <InjuryReportBody block={block} now={now} codes={codes} target={target} />}
        </div>
      )}
    </div>
  );
}

export function GameInjuries({ gameKey, teams }: { gameKey: string | null; teams?: readonly (string | null | undefined)[] }) {
  useCoreGameStateInterest(gameKey);
  const configured = useCoreGameStatesStore(s => s.configured);
  const key = coreGameStateKey(gameKey);
  const row = useCoreGameStatesStore(s => key ? s.injuries.get(key) : undefined);
  const status = useCoreGameStatesStore(s => s.status);
  const request = useInjuryDisclosureStore(s => (key && s.request?.gameKey === key ? s.request : null));
  const now = useSharedNow(1000, configured);
  if (!configured || !key || parseGameIdentity(key)?.sport !== 'nba') return null;
  return <GameInjuriesView key={key} row={row} status={status} now={now} request={request} teams={teams} />;
}

/**
 * #1398 — the Moneyline section's compact report line on Game Detail, shown
 * while the reader has injury panels on. It is the full report (exact ET and
 * local time, every row with reason, status-since and linkage) behind one
 * held line, and the explicit fallback wherever the ladder panels have no
 * room (compact view, collapsed or narrow ladders). While panels are drawn it
 * carries only what they do not: the report's slot and age, and the counts
 * of any team without a panel (#1400). It does not answer prop
 * jumps: those go to the panel's own disclosure (`GameInjuries`), and two
 * answering would open both.
 */
export function SectionInjuries({ gameKey, teams, paneled }: {
  gameKey: string | null;
  teams?: readonly (string | null | undefined)[];
  /** Teams whose ladder panel is drawn (#1400): see `GameInjuriesView`. */
  paneled?: readonly string[];
}) {
  const base = gameKey ? baseGameKey(gameKey) : null;
  useCoreGameStateInterest(base);
  const configured = useCoreGameStatesStore(s => s.configured);
  const key = coreGameStateKey(base);
  const row = useCoreGameStatesStore(s => key ? s.injuries.get(key) : undefined);
  const status = useCoreGameStatesStore(s => s.status);
  const now = useSharedNow(1000, configured);
  if (!configured || !key || parseGameIdentity(key)?.sport !== 'nba') return null;
  return <GameInjuriesView key={key} row={row} status={status} now={now} teams={teams} held paneled={paneled} />;
}

/**
 * The board's inline report for one chosen game (see the header): a labelled,
 * bounded, internally scrolling section with a Close control. Escape or Close
 * calls `onClose`, which returns focus to the board entry that opened it.
 */
export function BoardInjuryReport({ id, gameKey, title, teams, onClose }: {
  id: string;
  gameKey: string;
  /** Names the game, e.g. "New York Knicks @ Philadelphia 76ers". */
  title: string;
  teams?: readonly (string | null | undefined)[];
  onClose: () => void;
}) {
  const key = coreGameStateKey(baseGameKey(gameKey));
  const row = useCoreGameStatesStore(s => key ? s.injuries.get(key) : undefined);
  const status = useCoreGameStatesStore(s => s.status);
  const now = useSharedNow(1000);
  const headingId = useId();
  const block = row?.block ?? null;
  const codes = block ? orderedTeams(block, teams) : [];
  const warning = block ? injuryEvidenceWarning(row, status, now) : null;
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape') return;
    // Answered here, never by the board: Escape must not clear the aim.
    e.stopPropagation();
    e.preventDefault();
    onClose();
  };
  return (
    <section
      id={id}
      aria-labelledby={headingId}
      data-lt2-owner=""
      data-testid="board-injury-report"
      onKeyDown={onKeyDown}
      onClick={(e) => e.stopPropagation()}
      className="border-border bg-card my-1 max-w-2xl min-w-0 rounded-md border text-xs"
    >
      <div className="border-border flex min-w-0 flex-wrap items-baseline gap-x-2 border-b px-3 py-1.5">
        <h3 id={headingId} className="min-w-0 font-semibold break-words">Injury report · {title}</h3>
        {block && <span className="text-muted-foreground">report {reportAgeShort(block.reportAt, now)}</span>}
        {warning && <span role="status" className="text-amber-500 light:text-amber-700">{warning}</span>}
        <button type="button" onClick={onClose} className="ml-auto rounded px-1 font-medium hover:underline focus-visible:outline focus-visible:outline-1">
          Close
        </button>
      </div>
      <div
        role="region"
        aria-label="Injury report details"
        tabIndex={0}
        className="max-h-64 overflow-auto overscroll-contain px-3 pb-2 break-words [overflow-wrap:anywhere]"
      >
        {!block ? <p className="text-muted-foreground">No injury report is available for this game.</p>
          : <InjuryReportBody block={block} now={now} codes={codes} target={null} />}
      </div>
    </section>
  );
}
